import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import API from "@/lib/api";
import { toast } from "sonner";
import { DownloadSimple, FilePdf, FileCsv } from "@phosphor-icons/react";

const DATE_PRESETS = [
  { key: "all", label: "All time" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "custom", label: "Custom" },
];

function presetToRange(key) {
  if (key === "all") return { date_from: null, date_to: null };
  const now = new Date();
  if (key === "7d") {
    const from = new Date(now.getTime() - 7 * 86400000);
    return { date_from: from.toISOString(), date_to: null };
  }
  if (key === "30d") {
    const from = new Date(now.getTime() - 30 * 86400000);
    return { date_from: from.toISOString(), date_to: null };
  }
  return { date_from: null, date_to: null };
}

export function MessageExportDialog({ open, onOpenChange, caseId, selectedIds }) {
  const [format, setFormat] = useState("pdf");
  const [includeClient, setIncludeClient] = useState(true);
  const [includeReferrer, setIncludeReferrer] = useState(true);
  const [datePreset, setDatePreset] = useState("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [busy, setBusy] = useState(false);

  const onlySelected = selectedIds && selectedIds.length > 0;

  const handleExport = async () => {
    if (!includeClient && !includeReferrer && !onlySelected) {
      toast.error("Select at least one channel");
      return;
    }
    setBusy(true);
    try {
      const channels = [];
      if (includeClient) channels.push("client");
      if (includeReferrer) channels.push("referrer");
      let body = { format, channels };
      if (onlySelected) {
        // selectedIds: array of { id, source: "case_note" | "referrer_note" }
        body.message_ids = selectedIds;
      } else if (datePreset === "custom") {
        body.date_from = customFrom ? new Date(customFrom).toISOString() : null;
        body.date_to = customTo ? new Date(customTo + "T23:59:59").toISOString() : null;
      } else {
        const r = presetToRange(datePreset);
        body.date_from = r.date_from;
        body.date_to = r.date_to;
      }
      const resp = await API.post(`/cases/${caseId}/messages/export`, body, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `messages_${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export downloaded");
      onOpenChange(false);
    } catch {
      toast.error("Failed to export");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="message-export-dialog">
        <DialogHeader>
          <DialogTitle>Export Messages</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {/* Scope */}
          {onlySelected ? (
            <div className="text-xs bg-blue-50 border border-blue-200 text-blue-700 rounded p-3" data-testid="export-scope-selected">
              Exporting <strong>{selectedIds.length}</strong> selected message{selectedIds.length === 1 ? "" : "s"}
            </div>
          ) : (
            <>
              <div>
                <Label className="text-xs font-medium text-slate-700 mb-2 block">Channels</Label>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={includeClient} onCheckedChange={setIncludeClient} data-testid="export-client-channel" />
                    <span className="text-slate-700">Client conversation</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={includeReferrer} onCheckedChange={setIncludeReferrer} data-testid="export-referrer-channel" />
                    <span className="text-slate-700">Referrer conversation</span>
                  </label>
                </div>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-700 mb-2 block">Date range</Label>
                <div className="flex flex-wrap gap-1.5">
                  {DATE_PRESETS.map((p) => (
                    <button key={p.key} onClick={() => setDatePreset(p.key)}
                      data-testid={`export-preset-${p.key}`}
                      className={`px-3 py-1 text-xs rounded-md border ${datePreset === p.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {datePreset === "custom" && (
                  <div className="flex gap-2 mt-2">
                    <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} data-testid="export-custom-from" />
                    <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} data-testid="export-custom-to" />
                  </div>
                )}
              </div>
            </>
          )}
          {/* Format */}
          <div>
            <Label className="text-xs font-medium text-slate-700 mb-2 block">Format</Label>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setFormat("pdf")} data-testid="export-format-pdf"
                className={`flex flex-col items-center gap-1 px-3 py-3 rounded-md border ${format === "pdf" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                <FilePdf weight="bold" className="w-5 h-5" />
                <span className="text-xs">PDF</span>
              </button>
              <button onClick={() => setFormat("csv")} data-testid="export-format-csv"
                className={`flex flex-col items-center gap-1 px-3 py-3 rounded-md border ${format === "csv" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                <FileCsv weight="bold" className="w-5 h-5" />
                <span className="text-xs">CSV</span>
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
            <Button onClick={handleExport} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="export-confirm-btn">
              <DownloadSimple weight="bold" className="w-3.5 h-3.5 mr-1.5" />
              {busy ? "Generating..." : "Download"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
